import { create } from 'zustand';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useGroups } from './groups';
import { Profile } from '../types';

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
    return (crypto as any).randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
  });
}

function getDeviceName(): string {
  const ua = navigator.userAgent;
  let os = 'Ukjent OS';
  if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'Mac';
  else if (/Linux/.test(ua)) os = 'Linux';
  let browser = 'Ukjent nettleser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
  return `${browser} på ${os}`;
}

async function isTrustedDevice(userId: string): Promise<boolean> {
  const token = localStorage.getItem(`ts_token_${userId}`);
  if (!token) return false;
  const { data } = await supabase
    .from('trusted_devices')
    .select('id, expires_at')
    .eq('token', token)
    .eq('user_id', userId)
    .maybeSingle();
  if (!data) { localStorage.removeItem(`ts_token_${userId}`); return false; }
  if (new Date(data.expires_at) < new Date()) {
    localStorage.removeItem(`ts_token_${userId}`);
    await supabase.from('trusted_devices').delete().eq('id', data.id);
    return false;
  }
  await supabase.from('trusted_devices').update({ last_used_at: new Date().toISOString() }).eq('id', data.id);
  return true;
}

function translateAuthError(message: string): string {
  const map: Record<string, string> = {
    'New password should be different from the old password.': 'Det nye passordet må være forskjellig fra det gamle.',
    'Password should be at least 6 characters.': 'Passordet må være minst 6 tegn.',
    'Auth session missing!': 'Sesjonen er utløpt eller ugyldig. Trykk lenken i e-posten på nytt.',
    'For security purposes, you can only request this after 60 seconds.': 'Av sikkerhetshensyn kan du kun be om dette igjen etter 60 sekunder.',
  };
  return map[message] || message;
}

function isPasswordRecoveryUrl(): boolean {
  if (typeof window === 'undefined') return false;
  // Implisitt-flyt-gjenopprettingslenker har `type=recovery` i hash-fragmentet
  // (f.eks. #access_token=...&type=recovery). Sjekkes synkront, FØR noe
  // supabase.auth-kall gjøres — Supabase sitt eget PASSWORD_RECOVERY-varsel via
  // onAuthStateChange fyres internt med setTimeout(fn, 0) og kan rekke å komme
  // for sent i forhold til vår egen innloggingslogikk (observert i praksis: et
  // kort glimt av «Sett nytt passord»-skjermen før appen likevel logget inn).
  return /type=recovery/.test(window.location.hash) || /type=recovery/.test(window.location.search);
}

async function setTrustedDevice(userId: string): Promise<void> {
  const token = generateUUID();
  const expiresAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase.from('trusted_devices').insert({
    user_id: userId,
    token,
    device_name: getDeviceName(),
    expires_at: expiresAt,
  }).select('id').single();
  if (!error && data) {
    localStorage.setItem(`ts_token_${userId}`, token);
    localStorage.setItem(`ts_device_id_${userId}`, data.id);
  }
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  mfaRequired: boolean;
  mfaFactorId: string | null;
  mfaEnrolled: boolean;
  pendingRememberDevice: boolean;
  passwordRecovery: boolean;

  init: () => Promise<void>;
  signUp: (email: string, password: string, fullName: string, emailRedirectTo?: string, kommune?: string, fylke?: string) => Promise<string | null>;
  signIn: (email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
  updateProfile: (data: Partial<Profile>) => Promise<void>;
  deleteAccount: () => Promise<string | null>;
  resetPassword: (email: string) => Promise<string | null>;
  updatePassword: (newPassword: string) => Promise<string | null>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<string | null>;

  verifyMfa: (code: string, rememberDevice?: boolean) => Promise<string | null>;
  sendMfaRecovery: () => Promise<string | null>;
  enrollMfa: () => Promise<{ qr: string; secret: string; factorId: string } | null>;
  confirmMfaEnrollment: (factorId: string, code: string) => Promise<string | null>;
  unenrollMfa: (factorId: string) => Promise<string | null>;
  getMfaStatus: () => Promise<{ enrolled: boolean; factorId: string | null; pendingFactorId: string | null }>;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  profile: null,
  loading: true,
  mfaRequired: false,
  mfaFactorId: null,
  mfaEnrolled: false,
  pendingRememberDevice: false,
  passwordRecovery: isPasswordRecoveryUrl(),

  init: async () => {
    // Har vi allerede synkront (fra selve URL-en) fastslått at dette er en
    // gjenopprettingslenke? Da skal INGEN normal innloggingslogikk kjøre — vi
    // venter til brukeren faktisk kaller updatePassword().
    const startedInRecovery = get().passwordRecovery;

    // Registrer lytteren FØR getSession() — en PASSWORD_RECOVERY-hendelse fra en
    // gjenopprettingslenke i URL-en kan fyre allerede under klientens egen
    // initialisering, og ville gått tapt hvis vi abonnerte for sent.
    supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // IKKE logg brukeren rett inn — vis «Sett nytt passord»-skjermen i AuthPage i stedet
        set({ passwordRecovery: true, loading: false });
        return;
      }
      if (get().passwordRecovery && event !== 'USER_UPDATED') {
        // Supabase fyrer alltid en ekstra INITIAL_SESSION (eller lignende) rett etter
        // PASSWORD_RECOVERY, med den samme (nå etablerte) gjenopprettingssesjonen —
        // ikke la den hendelsen smette forbi «Sett nytt passord»-skjermen. Kun en
        // eksplisitt USER_UPDATED (etter at passordet faktisk er byttet) skal slippe gjennom.
        return;
      }
      if (session?.user) {
        // Sjekk om MFA kreves før vi setter bruker — hindrer at appen åpner seg uten 2FA
        const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (aalData?.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
          const { data: factors } = await supabase.auth.mfa.listFactors();
          const totpFactor = factors?.totp?.[0];
          if (totpFactor) {
            if (await isTrustedDevice(session.user.id)) {
              // Betrodd enhet — hopp over 2FA-skjermen
            } else {
              set({ mfaRequired: true, mfaFactorId: totpFactor.id });
              return;
            }
          }
        }

        if (event === 'MFA_CHALLENGE_VERIFIED') {
          const { pendingRememberDevice } = get();
          if (pendingRememberDevice) await setTrustedDevice(session.user.id);
          set({ pendingRememberDevice: false });
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const enrolled = (factors?.all ?? []).some(f => f.factor_type === 'totp' && f.status === 'verified');
        set({ user: session.user, session, profile, mfaRequired: false, mfaFactorId: null, mfaEnrolled: enrolled, passwordRecovery: false });
        if (event !== 'TOKEN_REFRESHED' && event !== 'USER_UPDATED') {
          const { fetchGroups } = useGroups.getState();
          await fetchGroups(session.user.id);
        }
      } else {
        set({ user: null, session: null, profile: null, mfaRequired: false, mfaFactorId: null });
      }
    });

    if (startedInRecovery) {
      // Vent til Supabase-klienten har hentet gjenopprettingssesjonen fra URL-en
      // internt (skjer i getSession()), men IKKE etabler normal innlogging herfra —
      // det skjer utelukkende via updatePassword() når passordet faktisk er byttet.
      await supabase.auth.getSession();
      set({ loading: false });
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (get().passwordRecovery) return; // Lytteren over rakk allerede å fange PASSWORD_RECOVERY
    let mfaNeeded = false;

    if (session?.user) {
      // Sjekk om AAL1-sesjon trenger MFA-oppgradering
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalData?.nextLevel === 'aal2' && aalData.currentLevel === 'aal1') {
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const totpFactor = factors?.totp?.[0];
        if (totpFactor) {
          if (await isTrustedDevice(session.user.id)) {
            // Betrodd enhet — hopp over 2FA-skjermen
          } else {
            set({ mfaRequired: true, mfaFactorId: totpFactor.id, loading: false });
            mfaNeeded = true;
          }
        }
      }
      if (!mfaNeeded) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        const { data: factors } = await supabase.auth.mfa.listFactors();
        const enrolled = (factors?.all ?? []).some(f => f.factor_type === 'totp' && f.status === 'verified');
        set({ user: session.user, session, profile, loading: false, mfaEnrolled: enrolled });
        const { fetchGroups } = useGroups.getState();
        await fetchGroups(session.user.id);
      }
    } else {
      set({ loading: false });
    }
  },

  signUp: async (email, password, fullName, emailRedirectTo, kommune, fylke) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, kommune: kommune || null, fylke: fylke || null },
        emailRedirectTo: emailRedirectTo || 'https://app.toolshare.no',
      }
    });
    if (error) return error.message;
    return null;
  },

  signIn: async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return error.message;
    return null;
  },

  sendMfaRecovery: async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return 'Ikke innlogget';
    try {
      const res = await fetch('https://hzplqizxzdwmfdopedjf.supabase.co/functions/v1/mfa-recovery', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'send' }),
      });
      const data = await res.json();
      if (!res.ok) return data.error || 'Sending feilet';
      return null;
    } catch {
      return 'Noe gikk galt';
    }
  },

  verifyMfa: async (code, rememberDevice = false) => {
    const { mfaFactorId } = get();
    if (!mfaFactorId) return 'Ingen aktiv faktor';
    set({ pendingRememberDevice: rememberDevice });
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: mfaFactorId,
      code,
    });
    if (error) { set({ pendingRememberDevice: false }); return 'Feil kode, prøv igjen'; }
    return null;
  },

  enrollMfa: async () => {
    const { data: existing } = await supabase.auth.mfa.listFactors();
    // Ubekreftede faktorer finnes bare i `all`, ikke i `totp`
    const unverified = (existing?.all ?? []).filter(f => f.factor_type === 'totp' && f.status !== 'verified');
    for (const factor of unverified) {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }

    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error) throw new Error(error.message);
    if (!data) throw new Error('Ingen data fra Supabase');
    return {
      qr: data.totp.qr_code,
      secret: data.totp.secret,
      factorId: data.id,
    };
  },

  confirmMfaEnrollment: async (factorId, code) => {
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
    if (error) return 'Feil kode, prøv igjen';
    set({ mfaEnrolled: true });
    return null;
  },

  unenrollMfa: async (factorId) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) return error.message;
    set({ mfaEnrolled: false });
    return null;
  },

  getMfaStatus: async () => {
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const all = factors?.all ?? [];
    const verified = all.find(f => f.factor_type === 'totp' && f.status === 'verified');
    const pending = all.find(f => f.factor_type === 'totp' && f.status !== 'verified');
    return {
      enrolled: !!verified,
      factorId: verified?.id ?? null,
      pendingFactorId: pending?.id ?? null,
    };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ user: null, session: null, profile: null, mfaRequired: false, mfaFactorId: null, mfaEnrolled: false, passwordRecovery: false });
  },

  resetPassword: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://app.toolshare.no',
    });
    if (error) return translateAuthError(error.message);
    return null;
  },

  updatePassword: async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return translateAuthError(error.message);
    // USER_UPDATED-hendelsen over dekker ikke fetchGroups (bevisst hoppet over for
    // vanlige profil-oppdateringer) — hent gruppene eksplisitt her slik at appen har
    // data klart med det samme brukeren går videre fra gjenopprettingsskjermen.
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { fetchGroups } = useGroups.getState();
      await fetchGroups(session.user.id);
    }
    return null;
  },

  changePassword: async (currentPassword, newPassword) => {
    const { user } = get();
    if (!user?.email) return 'Ikke innlogget';
    // Bekreft gjeldende passord først — Supabase krever ikke dette som standard
    // (secure_password_change er av), så vi verifiserer selv som en ekstra sperre
    // mot at noen med tilgang til en allerede innlogget økt kan overta kontoen.
    const { error: verifyError } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword });
    if (verifyError) return 'Feil nåværende passord';
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return translateAuthError(error.message);
    return null;
  },

  deleteAccount: async () => {
    const { session } = get();
    if (!session) return 'Ikke logget inn';
    try {
      const res = await fetch('https://hzplqizxzdwmfdopedjf.supabase.co/functions/v1/delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) return data.error || 'Sletting feilet';
      await supabase.auth.signOut();
      set({ user: null, session: null, profile: null });
      return null;
    } catch {
      return 'Noe gikk galt';
    }
  },

  updateProfile: async (data) => {
    const { user } = get();
    if (!user) return;
    await supabase.from('profiles').update(data).eq('id', user.id);
    set(s => ({ profile: s.profile ? { ...s.profile, ...data } : null }));
  },
}));
